import React, { useEffect, useRef } from 'react';
import { useParticipant, useMediaTrack, useDaily } from '@daily-co/daily-react';
import { MicrophoneIcon, VideoCameraIcon } from '@heroicons/react/24/solid';
import { MicrophoneIcon as MicrophoneOffIcon, VideoCameraSlashIcon } from '@heroicons/react/24/outline';

interface VideoTileProps {
  participantId: string;
  isLocal: boolean;
}

/**
 * Video tile component for displaying a participant's video/audio stream
 * Shows video when camera is on, fallback avatar when off
 */
export const VideoTile: React.FC<VideoTileProps> = ({ participantId, isLocal }) => {
  const participant = useParticipant(participantId);
  const videoTrack = useMediaTrack(participantId, 'video');
  const audioTrack = useMediaTrack(participantId, 'audio');
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Attach video track to video element
  useEffect(() => {
    if (videoRef.current && videoTrack?.persistentTrack) {
      const videoEl = videoRef.current;
      videoEl.srcObject = new MediaStream([videoTrack.persistentTrack]);
    }
  }, [videoTrack]);

  // Attach audio track to audio element (only for remote participants)
  useEffect(() => {
    if (!isLocal && audioRef.current && audioTrack?.persistentTrack) {
      const audioEl = audioRef.current;
      audioEl.srcObject = new MediaStream([audioTrack.persistentTrack]);
    }
  }, [audioTrack, isLocal]);

  if (!participant) {
    return null;
  }

  const { user_name, audio: isAudioOn, video: isVideoOn } = participant;
  const displayName = user_name || (isLocal ? 'You' : 'Guest');

  // Get initials for avatar
  const getInitials = (name: string): string => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div className="relative bg-gray-800 rounded-lg overflow-hidden aspect-video flex items-center justify-center">
      {/* Video element (shown when camera is on) */}
      {isVideoOn && (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={isLocal}
          className={`w-full h-full object-cover ${isLocal ? 'transform -scale-x-100' : ''}`}
        />
      )}

      {/* Audio element (for remote participants only) */}
      {!isLocal && <audio ref={audioRef} autoPlay playsInline />}

      {/* Fallback avatar (shown when camera is off) */}
      {!isVideoOn && (
        <div className="flex flex-col items-center justify-center">
          <div className="w-24 h-24 rounded-full bg-blue-600 flex items-center justify-center text-white text-3xl font-semibold mb-2">
            {getInitials(displayName)}
          </div>
          <p className="text-white text-lg">{displayName}</p>
        </div>
      )}

      {/* Name overlay */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
        <div className="flex items-center justify-between">
          <span className="text-white font-medium">
            {displayName}
            {isLocal && ' (You)'}
          </span>

          {/* Audio/video indicators */}
          <div className="flex items-center space-x-2">
            {/* Microphone indicator */}
            <div className={`p-1 rounded ${isAudioOn ? 'bg-green-600' : 'bg-red-600'}`}>
              {isAudioOn ? (
                <MicrophoneIcon className="w-4 h-4 text-white" />
              ) : (
                <MicrophoneOffIcon className="w-4 h-4 text-white" />
              )}
            </div>

            {/* Camera indicator */}
            <div className={`p-1 rounded ${isVideoOn ? 'bg-green-600' : 'bg-red-600'}`}>
              {isVideoOn ? (
                <VideoCameraIcon className="w-4 h-4 text-white" />
              ) : (
                <VideoCameraSlashIcon className="w-4 h-4 text-white" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Audio level indicator (visual feedback) */}
      {isAudioOn && audioTrack && (
        <div className="absolute top-2 right-2">
          <AudioLevelIndicator track={audioTrack.persistentTrack} />
        </div>
      )}
    </div>
  );
};

interface AudioLevelIndicatorProps {
  track: MediaStreamTrack | null | undefined;
}

/**
 * Simple audio level indicator that shows when someone is speaking
 */
const AudioLevelIndicator: React.FC<AudioLevelIndicatorProps> = ({ track }) => {
  const [isActive, setIsActive] = React.useState(false);

  useEffect(() => {
    if (!track) return;

    // This is a simplified version - in production you'd use Web Audio API
    // to actually measure audio levels
    const interval = setInterval(() => {
      setIsActive((prev) => !prev);
    }, 500);

    return () => clearInterval(interval);
  }, [track]);

  if (!isActive) return null;

  return (
    <div className="flex space-x-1">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="w-1 bg-green-500 rounded-full animate-pulse"
          style={{
            height: `${8 + i * 4}px`,
            animationDelay: `${i * 100}ms`,
          }}
        />
      ))}
    </div>
  );
};
